import React from 'react';
import { ProfitLossPoint } from '../types/billing';
import { formatAmountShort } from '../utils/formatters';

interface ProfitLossChartProps {
  data: ProfitLossPoint[];
}

// Modern SVG bar chart with improved styling
const ProfitLossChart: React.FC<ProfitLossChartProps> = ({ data }) => {
  const width = 900;
  const height = 400;
  const padding = { top: 20, right: 20, bottom: 60, left: 80 };

  const periods = data.map(d => d.period);
  const maxValue = Math.max(
    1,
    ...data.map(d => Math.max(d.billedAmount, d.receivedAmount))
  );

  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;
  const barGroupWidth = innerWidth / Math.max(1, periods.length);
  const barWidth = Math.max(12, (barGroupWidth - 20) / 2);

  const yScale = (v: number) => innerHeight - (v / maxValue) * innerHeight;

  // Modern color scheme
  const colors = {
    billed: '#3B82F6', // Blue-500
    received: '#10B981', // Emerald-500
    grid: '#F3F4F6', // Gray-100
    text: '#374151', // Gray-700
    textLight: '#6B7280', // Gray-500
    background: '#FAFAFA' // Gray-50
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl shadow-lg p-6 overflow-x-auto border border-gray-200">
      <div className="mb-4">
        <h4 className="text-lg font-semibold text-gray-800 mb-2">Revenue Analytics</h4>
        <p className="text-sm text-gray-600">Monthly comparison of billed vs received amounts</p>
      </div>
      
      <svg width={width} height={height} className="min-w-full">
        {/* Background */}
        <rect width={width} height={height} fill={colors.background} rx="8" />
        
        {/* Main chart area */}
        <g transform={`translate(${padding.left},${padding.top})`}>
          {/* Y axis grid lines */}
          {[0, 0.2, 0.4, 0.6, 0.8, 1].map((t, i) => {
            const y = innerHeight - t * innerHeight;
            const value = Math.round(maxValue * t);
            return (
              <g key={i}>
                <line 
                  x1={0} 
                  x2={innerWidth} 
                  y1={y} 
                  y2={y} 
                  stroke={colors.grid} 
                  strokeWidth={1}
                  strokeDasharray={i === 0 ? "none" : "2,2"}
                />
                <text 
                  x={-15} 
                  y={y} 
                  textAnchor="end" 
                  dominantBaseline="middle" 
                  fontSize={12} 
                  fill={colors.textLight}
                  fontWeight="500"
                >
                  {formatAmountShort(value)}
                </text>
              </g>
            );
          })}

          {/* X axis line */}
          <line 
            x1={0} 
            x2={innerWidth} 
            y1={innerHeight} 
            y2={innerHeight} 
            stroke={colors.textLight} 
            strokeWidth={2}
          />

          {/* Y axis line */}
          <line 
            x1={0} 
            x2={0} 
            y1={0} 
            y2={innerHeight} 
            stroke={colors.textLight} 
            strokeWidth={2}
          />

          {/* Bars with modern styling */}
          {data.map((d, idx) => {
            const x0 = idx * barGroupWidth + 10;
            const billedHeight = innerHeight - yScale(d.billedAmount);
            const receivedHeight = innerHeight - yScale(d.receivedAmount);
            
            return (
              <g key={idx}>
                {/* Billed amount bar */}
                <rect 
                  x={x0} 
                  y={yScale(d.billedAmount)} 
                  width={barWidth} 
                  height={billedHeight} 
                  fill={colors.billed}
                  rx="4"
                  ry="4"
                  className="hover:opacity-80 transition-opacity duration-200"
                >
                  <title>{`Billed: ${formatAmountShort(d.billedAmount)}`}</title>
                </rect>
                
                {/* Received amount bar */}
                <rect 
                  x={x0 + barWidth + 8} 
                  y={yScale(d.receivedAmount)} 
                  width={barWidth} 
                  height={receivedHeight} 
                  fill={colors.received}
                  rx="4"
                  ry="4"
                  className="hover:opacity-80 transition-opacity duration-200"
                >
                  <title>{`Received: ${formatAmountShort(d.receivedAmount)}`}</title>
                </rect>
                
                {/* Period labels */}
                <text 
                  x={x0 + barWidth + 4} 
                  y={innerHeight + 20} 
                  textAnchor="middle" 
                  fontSize={11} 
                  fill={colors.text}
                  fontWeight="500"
                >
                  {d.period}
                </text>
                
                {/* Value labels on bars */}
                {billedHeight > 20 && (
                  <text 
                    x={x0 + barWidth / 2} 
                    y={yScale(d.billedAmount) - 5} 
                    textAnchor="middle" 
                    fontSize={9} 
                    fill={colors.text}
                    fontWeight="600"
                  >
                    {formatAmountShort(d.billedAmount).replace('₹', '₹')}
                  </text>
                )}
                
                {receivedHeight > 20 && (
                  <text 
                    x={x0 + barWidth + 8 + barWidth / 2} 
                    y={yScale(d.receivedAmount) - 5} 
                    textAnchor="middle" 
                    fontSize={9} 
                    fill={colors.text}
                    fontWeight="600"
                  >
                    {formatAmountShort(d.receivedAmount).replace('₹', '₹')}
                  </text>
                )}
              </g>
            );
          })}

          {/* Modern Legend */}
          <g transform={`translate(${innerWidth - 200}, 10)`}>
            <rect x={0} y={0} width={14} height={14} fill={colors.billed} rx="3" ry="3" />
            <text x={20} y={11} fontSize={12} fill={colors.text} fontWeight="600">Billed Amount</text>
            <rect x={120} y={0} width={14} height={14} fill={colors.received} rx="3" ry="3" />
            <text x={140} y={11} fontSize={12} fill={colors.text} fontWeight="600">Received Amount</text>
          </g>
        </g>
      </svg>
    </div>
  );
};

export default ProfitLossChart;


